import { jsx as _jsx } from "react/jsx-runtime";
import { startOfDay } from 'date-fns';
import { useCallback, useEffect, useRef, useState, } from 'react';
import { styled } from 'styletron-react';
import { staticColors } from '../../../const/colors/staticColors';
import { useTestIdAttribute } from '../../../hooks/useTestIdAttribute';
import { Icon } from '../../Icon/Icon';
import { PlainButton } from '../../PlainButton/PlainButton';
import { Popover } from '../../Popover/Popover';
import { TextInput } from '../TextInput/TextInput';
import { Calendar } from './Calendar/Calendar';
import { formatDateToInputValue } from './Calendar/helpers/formatDateToInputValue';
import { parseInputToDate } from './Calendar/helpers/parseInputToDate';
const StyledTextInputWrapper = styled('div', {
    backgroundColor: staticColors.inputBackground,
});
export const DatePicker = ({ selectedValue: passedValue, labelText, inputPlaceholderText, getIsDisabled, minDate, maxDate, onDateChange, testId, nominativeMonths, name, error, readOnly, disabled, clearButtonText, todayButtonText, isRequiredLabel, }) => {
    const [selectedDate, setSelectedDate] = useState(passedValue ?? undefined);
    const [inputValue, setInputValue] = useState(formatDateToInputValue(passedValue));
    const isInternalChangeRef = useRef(false);
    useEffect(() => {
        if (isInternalChangeRef.current) {
            isInternalChangeRef.current = false;
            return;
        }
        const maybeNewDate = passedValue ?? undefined;
        setSelectedDate(maybeNewDate);
        setInputValue(formatDateToInputValue(maybeNewDate));
    }, [passedValue]);
    const [isOpenCalendar, setIsOpenCalendar] = useState(false);
    const isInteractive = !readOnly && !disabled;
    const handleOpenCalendar = useCallback(() => {
        if (!isInteractive)
            return;
        setIsOpenCalendar(true);
    }, [isInteractive]);
    const handleCloseCalendar = useCallback(() => {
        setIsOpenCalendar(false);
    }, []);
    const handleSelectDate = (date) => {
        isInternalChangeRef.current = true;
        setSelectedDate(date);
        setInputValue(formatDateToInputValue(date));
        onDateChange?.(date);
    };
    const handleClear = useCallback(() => {
        isInternalChangeRef.current = true;
        setSelectedDate(undefined);
        setInputValue('');
        onDateChange?.(undefined);
    }, [onDateChange]);
    const handleToday = useCallback(() => {
        const today = startOfDay(new Date());
        const isTodayDisabled = getIsDisabled?.(today) ?? false;
        const isTodayBeforeMin = minDate ? today < startOfDay(minDate) : false;
        const isTodayAfterMax = maxDate ? today > startOfDay(maxDate) : false;
        const isTodayBlocked = isTodayDisabled || isTodayBeforeMin || isTodayAfterMax;
        if (isTodayBlocked)
            return;
        isInternalChangeRef.current = true;
        setSelectedDate(today);
        setInputValue(formatDateToInputValue(today));
        onDateChange?.(today);
    }, [getIsDisabled, minDate, maxDate, onDateChange]);
    const testIdAttribute = useTestIdAttribute(testId);
    const handleInputChange = (event) => {
        const text = event.target.value;
        setInputValue(text);
        const result = parseInputToDate(text);
        if (result === undefined)
            return; // Still typing, wait for a complete input
        isInternalChangeRef.current = true;
        const isValidDate = result !== null && !isNaN(result.getTime());
        setSelectedDate(isValidDate ? result : undefined);
        // Null  → field cleared → pass undefined to the form
        // NaN Date → complete but invalid → Zod rejects it with an error
        // Valid Date → Zod validates min/max
        onDateChange?.(result === null ? undefined : result);
    };
    const calendarIcon = isInteractive ? (_jsx(PlainButton, { type: 'button', onClick: handleOpenCalendar, children: _jsx(Icon, { name: 'OutlineCalendarMonth', color: 'greyLight' }) })) : undefined;
    const handleInputClick = isInteractive ? handleOpenCalendar : undefined;
    const handleOnInputChange = isInteractive ? handleInputChange : undefined;
    const handleCalendarOnClear = clearButtonText ? handleClear : undefined;
    const handleCalendarOnToday = todayButtonText ? handleToday : undefined;
    const onContentOpenAutoFocus = (e) => e.preventDefault();
    const maybeMinDate = minDate ?? undefined;
    const maybeMaxDate = maxDate ?? undefined;
    return (_jsx(Popover, { open: isOpenCalendar, sameWidthAsAnchor: false, anchor: _jsx(StyledTextInputWrapper, { children: _jsx(TextInput, { afterElement: calendarIcon, disabled: disabled, error: error, isRequiredLabel: isRequiredLabel, label: labelText, name: name, placeholder: inputPlaceholderText, value: inputValue, readOnly: readOnly, onChange: handleOnInputChange, onClick: handleInputClick, ...testIdAttribute }) }), onContentOpenAutoFocus: onContentOpenAutoFocus, onClose: handleCloseCalendar, children: _jsx(Calendar, { clearButtonText: clearButtonText, getIsDisabled: getIsDisabled, maxDate: maybeMaxDate, minDate: maybeMinDate, nominativeMonths: nominativeMonths, selectedDate: selectedDate, todayButtonText: todayButtonText, onClear: handleCalendarOnClear, onCloseCalendar: handleCloseCalendar, onSelectDate: handleSelectDate, onToday: handleCalendarOnToday }) }));
};
